import type { ReactElement } from 'react'
import type { Entry } from 'contentful'
import React, { useCallback } from 'react'
import HeadingWithAnchor from '@/components/heading-with-anchor'
import ArticleHeader from '@/components/article-header'

import PageWrapper from '@/components/page-wrapper'
import RichText from '@/components/ui/rich-text'
import Quote from '@/components/ui/quote'
import Callout from '@/components/ui/resource/callout'
import ComponentImage from '@/components/ui/image'
import PrintWrapper from '@/components/print-wrapper'
import { isPrint } from '@/utils/print'
import { cx } from '@/utils/strings'
import SectionCta from '@/components/section/cta'
import { SetOfPricingComponent } from '@/components/section/set-of-pricing'
import SectionTabSection from '@/components/section/tab-section'
import { SetOfCardsSectionComponent } from '@/components/section/set-of-cards'
import { usePage } from '@inertiajs/react'
import Table from '@/components/table'
import Routes from '@/constants/routes'

import {
  isTypeComponentQuote,
  isTypeComponentTable,
  isTypeResourceCallout,
  isTypeResourceContent,
  isTypeResourceImage,
  isTypeSectionCta,
  isTypeSectionSetOfCards,
  isTypeSectionSetOfPricing,
  type TypeSeo,
  type TypeResourcePage,
} from '@/types/ctf'
import TocNav from '@/components/toc-nav'
import { useTranslations } from '@/utils/locale.ts'

type WrapperProps = {
  children: ReactElement
}

type ResourceNewProps = {
  post: TypeResourcePage<'WITHOUT_UNRESOLVABLE_LINKS'>
  defaultExtraContent: any
  headerNav: any[]
  footerNav: any[]
  headerBanners: any[]
  newsletterForm: any
  toc: any
}

export default function ResourcesNew(props: ResourceNewProps): React.JSX.Element {
  const { post, defaultExtraContent, headerNav, footerNav, headerBanners, newsletterForm, toc } = props

  const {
    content,
    description,
    downloadAsPdf,
    heroButtons,
    image,
    imageDarkMode,
    imageGradientOverlay,
    imageStyle = 'Icon',
    seo: _seo,
    title,
    extraContent,
    extraContentPosition = 'Bottom',
    thirdPartyLogo,
    resourceTags,
    hideDefaultPricing = false,
    heroBackgroundColor,
    includeToc = true,
  } = post.fields

  const seo = _seo
    ? ({
        fields: {
          title: _seo.fields?.title || title,
          description: _seo.fields?.description || description,
          ogImage: _seo.fields?.ogImage,
        },
      } as TypeSeo<'WITHOUT_UNRESOLVABLE_LINKS'>)
    : ({ fields: { title, description } } as TypeSeo<'WITHOUT_UNRESOLVABLE_LINKS'>)

  const { url } = usePage()
  const { t } = useTranslations()

  const isPrintView = isPrint()
  const pathname = url.split('?')[0]

  const hasCallouts = content?.some((section) => {
    return (
      section &&
      isTypeResourceContent(section) &&
      Array.isArray(section.fields.callouts) &&
      section.fields.callouts?.length > 0
    )
  })

  const breadcrumb = [{ name: t('Resources'), path: '/resources/' }, { name: title }]

  const CheckPricingExtraContent = (obj: Entry | undefined) => obj && isTypeSectionSetOfPricing(obj)

  const renderExtraContent = () => {
    return extraContent?.map((section) => {
      if (!section) return null
      if (isTypeSectionSetOfPricing(section)) {
        return (
          <SetOfPricingComponent
            key={section.sys.id}
            {...section.fields}
            internalTitle={section.fields.internalTitle || section.sys.id}
          />
        )
      }

      if (isTypeSectionCta(section))
        return (
          <SectionCta
            key={section.sys.id}
            {...section.fields}
            internalName={section.fields.internalName || section.sys.id}
          />
        )
      return null
    })
  }

  const isBitwardenSecurityPerspectives = !!resourceTags?.find((type) => type?.fields.slug === 'security-perspectives')

  const Wrapper = useCallback(
    ({ children }: WrapperProps) =>
      isPrintView ? (
        <PrintWrapper
          title={title}
          description={description}
          section={t('Resource Center')}
          thirdPartyLogo={thirdPartyLogo}
          pageUrl={Routes.hostname + pathname}
          securityPerspectives={isBitwardenSecurityPerspectives}
          coverImage={isBitwardenSecurityPerspectives ? image : undefined}
        >
          {children}
        </PrintWrapper>
      ) : (
        <PageWrapper
          seo={seo}
          breadcrumb={breadcrumb}
          headerNav={headerNav}
          footerNav={footerNav}
          headerBanners={headerBanners}
          newsletterForm={newsletterForm}
        >
          <ArticleHeader
            image={image}
            imageDarkMode={imageDarkMode}
            imageOverlay={imageGradientOverlay}
            imageStyle={imageStyle}
            publishDate={null}
            title={title}
            description={description}
            tagline={{
              text: isBitwardenSecurityPerspectives ? t('Bitwarden Security Perspectives') : t('Bitwarden Resources'),
              url: isBitwardenSecurityPerspectives ? '/resources/tag/security-perspectives/' : '/resources/',
            }}
            downloadAsPdf={downloadAsPdf}
            heroButtons={heroButtons}
            backgroundColor={heroBackgroundColor}
          />

          <div className="md:py-10 lg:pb-20">
            {extraContent && <div className="mb-8">{extraContentPosition === 'Top' && renderExtraContent()}</div>}
            {children}
          </div>
          <div className="pb-20">
            {extraContentPosition === 'Above Pricing' && renderExtraContent()}
            {((extraContent && !extraContent.some(CheckPricingExtraContent) && !hideDefaultPricing) ||
              (!extraContent && !hideDefaultPricing)) && <SectionTabSection {...defaultExtraContent.fields} />}
            {(!extraContentPosition || extraContentPosition === 'Bottom') && renderExtraContent()}
          </div>
        </PageWrapper>
      ),
    [isPrintView]
  )

  const renderCallout = (section) => (
    <div className="flex flex-col">
      {section.fields.callouts?.map((callout) => {
        if (isTypeResourceImage(callout) || !callout) return null

        if (isTypeComponentQuote(callout)) {
          return (
            <div key={callout.sys.id}>
              <Quote {...callout.fields} className={cx(isPrintView && ' bg-secondary-50!')} />
            </div>
          )
        }
        if (isTypeResourceCallout(callout)) {
          return <Callout key={callout.sys.id} contentful_id={callout.sys.id} {...callout.fields} />
        }

        return <div key={callout.sys.id}>unsupported node</div>
      })}
    </div>
  )

  const testForCallout = (section) => {
    return section.fields.callouts?.length && section.fields.callouts.length >= 1
  }

  return (
    <Wrapper>
      {isPrintView &&
        content?.map(
          (section) =>
            section && (
              <div key={section.sys.id}>
                <table>
                  <tbody>
                    <tr>
                      <td valign="top" className="p-0!" width={testForCallout(section) ? '70%' : '100%'}>
                        {section.fields.title && <h2>{section.fields.title}</h2>}
                        <RichText body={section.fields.body} />
                      </td>
                      {testForCallout(section) && (
                        <td valign="top" className="pr-0! pl-8!">
                          {renderCallout(section)}
                        </td>
                      )}
                    </tr>
                  </tbody>
                </table>
              </div>
            )
        )}
      {!isPrintView && (
        <article className="mx-auto max-w-8xl px-5 lg:px-9">
          <div className={cx('rounded-lg relative')}>
            <div className="flex gap-16">
              <div className={cx('lg:w-2/3')}>
                {toc && toc.length > 0 && (
                  <div className="mb-6 md:hidden">
                    <TocNav sections={toc} />
                  </div>
                )}
                {content?.map(
                  (section) =>
                    section && (
                      <>
                        {isTypeResourceContent(section) && (
                          <>
                            {section.fields.title && <HeadingWithAnchor type="h2" title={section.fields.title} />}

                            {section.fields.callouts?.some((callout) => callout && isTypeResourceImage(callout)) ? (
                              <>
                                <div className="lg:flex gap-8">
                                  <div className="flex-1">
                                    <RichText body={section.fields.body} />
                                  </div>
                                  <div className="flex-none sm:w-1/3">
                                    {section.fields.callouts?.map(
                                      (callout) =>
                                        callout && (
                                          <ComponentImage
                                            key={callout.sys.id}
                                            {...callout.fields}
                                            className="group mb-0 rounded-lg"
                                          />
                                        )
                                    )}
                                  </div>
                                </div>
                                {section.fields.callouts?.some(
                                  (callout) => callout && !isTypeResourceImage(callout)
                                ) && <div className="mt-6 mb-10">{renderCallout(section)}</div>}
                              </>
                            ) : (
                              <>
                                <RichText body={section.fields.body} />
                                {hasCallouts && renderCallout(section)}
                              </>
                            )}
                          </>
                        )}

                        {isTypeSectionSetOfCards(section) && (
                          <div
                            className={cx(
                              '&>section]:mb-10 lg:[&>section]:mb-20 [&>section]:mt-10 break-after-avoid-page',
                              isPrintView &&
                                '[*]:bg-transparent! [&>section]:mb-20! [.set-of-cards-container]:(px-0! block!) [.card-container]:px-0!'
                            )}
                          >
                            <SetOfCardsSectionComponent key={section.sys.id} {...section.fields} textLeft imageTop />
                          </div>
                        )}

                        {isTypeComponentTable(section) && (
                          <div
                            key={section.sys.id}
                            className={cx(
                              `break-inside-avoid`,
                              ...(isPrintView
                                ? [
                                    `**:bg-transparent! [&_table *]:!text-xs [&_th,&_td]:p-[1rem 0.5rem]`,
                                    `[&_.table-header-cell-container]:gap-2 [&_.table-header-icon]:w-8 [&_.table-header-icon]:h-8 [&_.table-header-icon svg]:!fill-none`,
                                  ]
                                : [])
                            )}
                          >
                            <Table {...section.fields} />
                          </div>
                        )}
                      </>
                    )
                )}
              </div>
              <div className={cx('hidden md:block lg:w-1/3')}>
                {includeToc && toc && toc.length > 0 && (
                  <div className="sticky top-36 py-2 px-6 rounded-lg border mt-4">
                    <TocNav sections={toc} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </article>
      )}
    </Wrapper>
  )
}
